import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShoppingBag,
  CheckCircle,
  ArrowRight,
  Wrench,
  Bot,
  Sparkles,
  Zap,
  Clock
} from 'lucide-react';
import { authService } from '../../services/authService';
import { apiClient } from '../../services/apiClient';

const Products = () => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState({ cmms: false, chatbot: false });
  // 'TRIAL' | 'ACTIVE' | null
  const [subscriptionStatus, setSubscriptionStatus] = useState({ cmms: null, chatbot: null });

  useEffect(() => {
    const hasCmms = localStorage.getItem('hasCmmsSubscription') === 'true';
    const hasChatbot = localStorage.getItem('hasChatbotSubscription') === 'true';
    setSubscriptions({ cmms: hasCmms, chatbot: hasChatbot });

    const user = authService.getCurrentUser();
    const companyId = user?.companyId;
    if (!companyId) return;

    if (hasCmms) {
      apiClient.get(`/api/subscriptions/cmms/company/${companyId}`)
        .then(data => setSubscriptionStatus(prev => ({ ...prev, cmms: data?.status ?? null })))
        .catch(() => {});
    }
    if (hasChatbot) {
      apiClient.get(`/api/subscriptions/chatbot/company/${companyId}`)
        .then(data => setSubscriptionStatus(prev => ({ ...prev, chatbot: data?.status ?? null })))
        .catch(() => {});
    }
  }, []);

  // Product catalog - can be extended with more products
  const products = [
    {
      id: 'cmms',
      name: t('products.cmmsTitle'),
      fullName: t('products.cmmsFullName'),
      description: t('products.cmmsProductDescription'),
      icon: Wrench,
      color: 'from-blue-500 to-blue-600',
      subscribed: subscriptions.cmms,
      isTrial: subscriptionStatus.cmms === 'TRIAL',
      features: [
        t('products.cmmsFeature1'),
        t('products.cmmsFeature2'),
        t('products.cmmsFeature3'),
        t('products.cmmsFeature4'),
        t('products.cmmsFeature5'),
        t('products.cmmsFeature6'),
      ],
      plansLink: '/portal/cmms/plans',
      overviewLink: '/portal/cmms',
      upgradeLink: '/portal/billing?upgrade=CMMS',
    },
    {
      id: 'chatbot',
      name: t('products.chatbotTitle'),
      fullName: t('products.chatbotDescription'),
      description: t('products.chatbotProductDescription'),
      icon: Bot,
      color: 'from-purple-500 to-purple-600',
      subscribed: subscriptions.chatbot,
      isTrial: subscriptionStatus.chatbot === 'TRIAL',
      features: [
        t('products.chatbotFeature1'),
        t('products.chatbotFeature2'),
        t('products.chatbotFeature3'),
        t('products.chatbotFeature4'),
        t('products.chatbotFeature5'),
        t('products.chatbotFeature6'),
      ],
      plansLink: '/portal/chatbot/plans',
      overviewLink: '/portal/chatbot',
      upgradeLink: '/portal/billing?upgrade=CHATBOT',
    },
    // Future products can be added here
    // {
    //   id: 'crm',
    //   name: 'CRM',
    //   fullName: 'Customer Relationship Management',
    //   description: 'Manage customer relationships and sales pipeline',
    //   icon: Users,
    //   color: 'from-green-500 to-green-600',
    //   subscribed: false,
    //   features: ['Contact management', 'Sales pipeline', 'Email integration'],
    //   plansLink: '/portal/crm/plans',
    //   overviewLink: '/portal/crm'
    // },
  ];

  const subscribedProducts = products.filter(p => p.subscribed);
  const availableProducts = products.filter(p => !p.subscribed);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <ShoppingBag className="w-8 h-8" />
          {t('products.title')}
        </h1>
        <p className="text-gray-600 mt-2">
          {t('products.pageSubtitle')}
        </p>
      </div>

      {/* Subscribed Products */}
      {subscribedProducts.length > 0 && (
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t('products.yourActiveProducts')} ({subscribedProducts.length})
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subscribedProducts.map((product) => (
              <div
                key={product.id}
                className={`bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border-2 ${
                  product.isTrial ? 'border-amber-300' : 'border-green-200'
                }`}
              >
                {/* Badge */}
                {product.isTrial ? (
                  <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-700">{t('products.trialActive')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-50 border-b border-green-200 px-4 py-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-700">{t('products.currentPlan')}</span>
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <product.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-600 mt-0.5">{product.fullName}</p>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">{product.description}</p>

                  <div className="space-y-2 mb-6">
                    {product.features.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                    {product.features.length > 3 && (
                      <p className="text-xs text-gray-500 ml-6">
                        +{product.features.length - 3} {t('products.moreFeatures')}
                      </p>
                    )}
                  </div>

                  {product.isTrial ? (
                    <div className="flex flex-col gap-2">
                      <Link
                        to={product.upgradeLink}
                        className="w-full btn btn-primary flex items-center justify-center gap-2"
                      >
                        <Zap className="w-4 h-4" />
                        {t('products.upgradeToPaid')}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link
                        to={product.overviewLink}
                        className="w-full btn btn-outline flex items-center justify-center gap-2 text-sm"
                      >
                        {t('products.continueWithTrial')}
                      </Link>
                    </div>
                  ) : (
                    <Link
                      to={product.overviewLink}
                      className="w-full btn btn-primary flex items-center justify-center gap-2"
                    >
                      {t('products.getStarted')}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Products */}
      {availableProducts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t('products.availableProducts')} ({availableProducts.length})
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-xl shadow-sm border-2 border-gray-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                {/* Badge */}
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 border-b border-gray-200 px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary-600" />
                    <span className="text-sm font-semibold text-primary-700">{t('products.availableToSubscribe')}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <product.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-600 mt-0.5">{product.fullName}</p>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">{product.description}</p>

                  <div className="space-y-2 mb-6">
                    {product.features.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                    {product.features.length > 3 && (
                      <p className="text-xs text-gray-500 ml-6">
                        +{product.features.length - 3} {t('products.moreFeatures')}
                      </p>
                    )}
                  </div>

                  <Link
                    to={product.plansLink}
                    className="w-full btn btn-outline flex items-center justify-center gap-2 hover:bg-primary-600 hover:text-white hover:border-primary-600"
                  >
                    {t('products.subscribeNow')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Products Subscribed Message */}
      {subscribedProducts.length > 0 && availableProducts.length === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {t('products.allSetTitle')} 🎉
          </h3>
          <p className="text-gray-600">
            {t('products.allSetSubtitle')}
          </p>
        </div>
      )}

      {/* No Products Subscribed Message */}
      {subscribedProducts.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center mb-8 mt-4">
          <ShoppingBag className="w-16 h-16 text-amber-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {t('products.noActiveSubscriptions')}
          </h3>
          <p className="text-gray-600">
            {t('products.noActiveSubtitle')}
          </p>
        </div>
      )}
    </div>
  );
};

export default Products;
